'use client'

/*
 * Source of this hook:
 * https://gist.github.com/MartijnHols/e9f4f787efa9190885a708468f63c5bb
 */

import { useEffect, useState } from 'react'

import noop from 'lodash/fp/noop'

const getSize = () => {
  if (typeof window === 'undefined') {
    throw new Error('useViewportSize can only be used in the browser')
  }

  return {
    height: window.visualViewport?.height ?? window.innerHeight,
    width: window.visualViewport?.width ?? window.innerWidth,
  }
}

interface ViewportSize {
  height: number
  width: number
}

export default function useViewportSize() {
  const [viewportSize, setViewportSize] = useState<ViewportSize | undefined>()

  useEffect(() => {
    if (typeof window === 'undefined') {
      return noop
    }

    const handleResize = () => {
      setViewportSize(getSize())
    }

    window.addEventListener('resize', handleResize)
    // From the top of my head this used to be required for older browsers since
    // this didn't trigger a resize event. Keeping it in to be safe.
    window.addEventListener('orientationchange', handleResize)
    // This is needed on iOS to resize the viewport when the Virtual/OnScreen
    // Keyboard opens. This does not trigger any other event, or the standard
    // resize event.
    window.visualViewport?.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      window.visualViewport?.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    setViewportSize(getSize())
    // We want this effect to only run once.
  }, [])

  return viewportSize
}
